// The outlet's physical estate: collection sites (branches + DC), the bins at
// each site and the ultrasonic fill sensors on some of them.

import type { CollectionSiteSchema } from '../../types/collection-site.type';
import { ContainerStatus, type ContainerSchema } from '../../types/container.type';
import { SensorStatus, SensorType, type SensorReading, type SensorSchema } from '../../types/sensor.type';
import {
  DEMO_CONTAINERS,
  DEMO_MATERIAL_IDS,
  DEMO_OUTLET_ORG_ID,
  DEMO_SENSORS,
  DEMO_SITES,
  HOUR_MS,
  daysAgo,
  daysFromNow,
  hashString,
  ids,
  iso,
  type DemoContainer,
  type DemoSensor,
  type DemoSite,
} from '../core';
import { materialName, toMaterialType } from './catalog';
import { makeCounter, mapsUrl, nowIso, round1, tr, unit } from './common';

// ── Collection sites ───────────────────────────────────────────────────────

let sites: CollectionSiteSchema[] = [];
const siteCounter = makeCounter(100);

const PROVINCE: Record<string, [string, string]> = {
  Riyadh: ['Riyadh Province', 'منطقة الرياض'],
  Jeddah: ['Makkah Province', 'منطقة مكة المكرمة'],
};

const SITE_AREA_M2 = [4200, 2600, 2400, 18500, 3100, 2200];

function postalCode(address: string): string {
  return /(\d{5})\s*$/.exec(address)?.[1] ?? '';
}

function siteRecord(site: DemoSite, i: number): CollectionSiteSchema {
  const dc = site.site_type === 'distribution_center';
  const street = site.address.split(',')[0];
  return {
    id: site.id,
    latitude: site.lat,
    longitude: site.lng,
    google_maps_url: mapsUrl(site.lat, site.lng),
    area: SITE_AREA_M2[i] ?? 2500,
    organization_id: DEMO_OUTLET_ORG_ID,
    status: 'ACTIVE',
    created_at: iso(daysAgo(480 - i * 37)),
    updated_at: iso(daysAgo(6 + i * 3)),
    data: {
      name: site.name_en,
      name_ar: site.name_ar,
      code: site.code,
      type: dc ? 'INDUSTRIAL' : 'COMMERCIAL',
      site_type: site.site_type,
      address: {
        street,
        street_ar: site.address,
        city: site.city,
        city_ar: site.city_ar,
        state: PROVINCE[site.city][0],
        country: 'Saudi Arabia',
        postal_code: postalCode(site.address),
      },
      contact_info: {
        name: site.manager_name,
        phone: site.phone,
        email: `${site.code.toLowerCase()}@alnakheel.example`,
      },
      capacity: dc ? '40 t / week' : '6 t / week',
      operating_hours: site.opening_hours,
      schedule: {
        pickup_days: dc ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Saturday'] : ['Sunday', 'Tuesday', 'Thursday'],
        pickup_time: dc ? '05:30' : '06:30',
      },
      notes: dc
        ? 'Baled OCC and stretch film are staged at dock 4. Forklift available 24h.'
        : 'Recyclables are staged behind the loading bay; use the service entrance.',
      special_instructions: dc ? 'Report to the gate security office for a dock pass.' : 'Avoid pickups between 16:00 and 19:00 (store peak).',
    },
  };
}

function localizedSite(site: CollectionSiteSchema): CollectionSiteSchema {
  const data = site.data ?? {};
  const address = (data.address ?? {}) as Record<string, unknown>;
  return {
    ...site,
    data: {
      ...data,
      name: tr(String(data.name ?? ''), data.name_ar as string | undefined),
      address: { ...address, city: tr(String(address.city ?? ''), address.city_ar as string | undefined) },
    },
  };
}

export function listSites(orgId: string | null | undefined, search?: string): CollectionSiteSchema[] {
  const rows = sites.filter((s) => !orgId || s.organization_id === orgId).map(localizedSite);
  if (!search) return rows;
  const q = search.toLowerCase();
  return rows.filter((s) => String(s.data?.name ?? '').toLowerCase().includes(q) || String(s.data?.code ?? '').toLowerCase().includes(q));
}

export function siteRecordById(id: string | null | undefined): CollectionSiteSchema | undefined {
  return sites.find((s) => s.id === id);
}

export function siteDisplayName(id: string | null | undefined): string | null {
  const site = siteRecordById(id);
  if (!site) return null;
  return tr(String(site.data?.name ?? ''), site.data?.name_ar as string | undefined) || null;
}

export function siteAddress(id: string | null | undefined): string | null {
  const site = siteRecordById(id);
  if (!site) return null;
  const a = (site.data?.address ?? {}) as Record<string, string>;
  return [a.street, a.city].filter(Boolean).join(', ') || null;
}

export function getSite(id: string): CollectionSiteSchema | undefined {
  const s = siteRecordById(id);
  return s ? localizedSite(s) : undefined;
}

export function createSite(input: Record<string, unknown>): CollectionSiteSchema {
  const n = siteCounter.next();
  const data = (input.data ?? {}) as Record<string, unknown>;
  const site: CollectionSiteSchema = {
    id: ids.site(n),
    latitude: input.latitude as number | undefined,
    longitude: input.longitude as number | undefined,
    google_maps_url: (input.google_maps_url as string | undefined) ??
      (typeof input.latitude === 'number' && typeof input.longitude === 'number' ? mapsUrl(input.latitude, input.longitude) : undefined),
    area: input.area as number | undefined,
    organization_id: String(input.organization_id ?? DEMO_OUTLET_ORG_ID),
    status: String(input.status ?? 'ACTIVE'),
    created_at: nowIso(),
    updated_at: nowIso(),
    data: { ...data, name_ar: undefined },
  };
  sites.push(site);
  return localizedSite(site);
}

export function updateSite(id: string, patch: Record<string, unknown>): CollectionSiteSchema | undefined {
  const site = siteRecordById(id);
  if (!site) return undefined;
  for (const key of ['latitude', 'longitude', 'google_maps_url', 'area', 'status', 'organization_id'] as const) {
    if (patch[key] !== undefined) (site as unknown as Record<string, unknown>)[key] = patch[key];
  }
  if (patch.data && typeof patch.data === 'object') {
    const next = patch.data as Record<string, unknown>;
    // An edited name replaces both language variants.
    site.data = { ...site.data, ...next, ...(typeof next.name === 'string' ? { name_ar: next.name } : {}) };
  }
  site.updated_at = nowIso();
  return localizedSite(site);
}

export function deleteSite(id: string): boolean {
  const before = sites.length;
  sites = sites.filter((s) => s.id !== id);
  containers = containers.filter((c) => c.collection_site_id !== id);
  return sites.length !== before;
}

// ── Containers ─────────────────────────────────────────────────────────────

/** Rough bulk density (kg per litre) of each stream as it sits in a bin. */
const DENSITY_KG_PER_L: Record<string, number> = {
  [DEMO_MATERIAL_IDS.occ]: 0.09,
  [DEMO_MATERIAL_IDS.mixed_paper]: 0.14,
  [DEMO_MATERIAL_IDS.office_paper]: 0.2,
  [DEMO_MATERIAL_IDS.pet]: 0.035,
  [DEMO_MATERIAL_IDS.hdpe]: 0.05,
  [DEMO_MATERIAL_IDS.ldpe_film]: 0.06,
  [DEMO_MATERIAL_IDS.aluminium_cans]: 0.03,
  [DEMO_MATERIAL_IDS.uco]: 0.92,
  [DEMO_MATERIAL_IDS.wood_pallets]: 0.18,
};

const LOCATIONS = ['Loading bay', 'Back-of-house store', 'Kitchen yard', 'Dock 4', 'Service corridor', 'Car park recycling corner'];
const LOCATIONS_AR = ['منطقة التحميل', 'المستودع الخلفي', 'ساحة المطبخ', 'رصيف 4', 'ممر الخدمات', 'ركن التدوير في المواقف'];

type StoredContainer = ContainerSchema & { fill_percent: number; capacity_kg: number; sensor_id: string | null };

let containers: StoredContainer[] = [];
const containerCounter = makeCounter(100);

export function capacityKgFor(materialId: string, litres: number): number {
  return Math.round(litres * (DENSITY_KG_PER_L[materialId] ?? 0.1));
}

function containerRecord(c: DemoContainer, index: number): StoredContainer {
  const site = DEMO_SITES.find((s) => s.id === c.site_id)!;
  const sensor = DEMO_SENSORS.find((s) => s.id === c.sensor_id);
  const capacityKg = capacityKgFor(c.material_id, c.capacity_litres);
  const angle = (hashString(c.id) % 360) * (Math.PI / 180);
  const offset = 0.00025 + unit(c.id) * 0.0004;
  const locIdx = hashString(c.code) % LOCATIONS.length;
  return {
    id: c.id,
    device_id: sensor ? sensor.serial_number : `NKH-${c.code}`,
    material_type_id: c.material_id,
    material_type: toMaterialType(c.material_id, DEMO_OUTLET_ORG_ID),
    status: ContainerStatus.PENDING,
    latitude: Number((site.lat + Math.sin(angle) * offset).toFixed(6)),
    longitude: Number((site.lng + Math.cos(angle) * offset).toFixed(6)),
    buffer: 15,
    collection_site_id: site.id,
    height: c.capacity_litres === 2500 ? 180 : c.capacity_litres === 1100 ? 135 : 120,
    temperature: sensor ? 24 + (hashString(c.id) % 14) : undefined,
    angle: sensor ? hashString(c.id) % 4 : undefined,
    full_alarm: c.fill_level >= 90,
    fire_alarm: false,
    tilt_alarm: false,
    battery_alarm: sensor?.status === 'low_battery',
    volt: sensor ? round1(2.9 + (sensor.battery_pct / 100) * 0.8) : undefined,
    rsrp: sensor?.signal_dbm,
    frame_counter: sensor ? 18000 + (hashString(c.id) % 9000) : undefined,
    fill_level: Math.round((c.fill_level / 100) * capacityKg),
    capacity: c.capacity_litres,
    data: {
      name: `${c.code} · ${c.capacity_litres.toLocaleString('en-US')} L`,
      code: c.code,
      capacity: capacityKg,
      capacity_unit: 'kg',
      capacity_litres: c.capacity_litres,
      location_name: LOCATIONS[locIdx],
      location_name_ar: LOCATIONS_AR[locIdx],
      fill_percent: c.fill_level,
      last_emptied_at: c.last_emptied_at,
      sensor_id: c.sensor_id,
    },
    organization_id: DEMO_OUTLET_ORG_ID,
    created_at: iso(daysAgo(430 - index * 11)),
    updated_at: sensor ? sensor.last_seen_at : c.last_emptied_at,
    fill_percent: c.fill_level,
    capacity_kg: capacityKg,
    sensor_id: c.sensor_id,
  };
}

function containerStatus(c: StoredContainer, awaitingPickup: ReadonlySet<string>): ContainerStatus {
  if (c.status === ContainerStatus.MAINTENANCE || c.status === ContainerStatus.INACTIVE) return c.status;
  if (awaitingPickup.has(c.id)) return ContainerStatus.AWAITING_PICKUP;
  if (c.fill_percent >= 80) return ContainerStatus.READY_FOR_PICKUP;
  if (c.fill_percent <= 12) return ContainerStatus.EMPTY;
  return ContainerStatus.PENDING;
}

export function serializeContainer(c: StoredContainer, awaitingPickup: ReadonlySet<string>): ContainerSchema {
  const { fill_percent: _fp, capacity_kg: _ck, sensor_id: _sid, ...rest } = c;
  void _fp;
  void _ck;
  void _sid;
  const data = rest.data ?? {};
  return {
    ...rest,
    status: containerStatus(c, awaitingPickup),
    material_type: toMaterialType(c.material_type_id, c.organization_id),
    data: {
      ...data,
      location_name: tr(String(data.location_name ?? ''), data.location_name_ar as string | undefined),
      material_name: materialName(c.material_type_id),
    },
  };
}

export function listContainerRecords(orgId: string | null | undefined): StoredContainer[] {
  return containers.filter((c) => !orgId || c.organization_id === orgId);
}

export function containerRecordById(id: string | null | undefined): StoredContainer | undefined {
  return containers.find((c) => c.id === id);
}

export function createContainer(input: Record<string, unknown>): StoredContainer {
  const n = containerCounter.next();
  const materialId = String(input.material_type_id ?? DEMO_MATERIAL_IDS.occ);
  const data = (input.data ?? {}) as Record<string, unknown>;
  const capacity = Number(data.capacity ?? 0) || capacityKgFor(materialId, 1100);
  const unitName = String(data.capacity_unit ?? 'kg');
  const capacityKg = unitName === 'tons' ? capacity * 1000 : capacity;
  const fillKg = Number(input.fill_level ?? 0) || 0;
  const record: StoredContainer = {
    id: ids.container(n),
    device_id: String(input.device_id ?? `NKH-NEW-${n}`),
    material_type_id: materialId,
    material_type: toMaterialType(materialId, String(input.organization_id ?? DEMO_OUTLET_ORG_ID)),
    status: (input.status as ContainerStatus) ?? ContainerStatus.PENDING,
    latitude: input.latitude as number | undefined,
    longitude: input.longitude as number | undefined,
    buffer: (input.buffer as number | undefined) ?? 15,
    collection_site_id: input.collection_site_id as string | undefined,
    fill_level: fillKg,
    data: { ...data, capacity: capacity, capacity_unit: unitName },
    organization_id: String(input.organization_id ?? DEMO_OUTLET_ORG_ID),
    created_at: nowIso(),
    updated_at: nowIso(),
    fill_percent: capacityKg > 0 ? Math.round((fillKg / capacityKg) * 100) : 0,
    capacity_kg: capacityKg,
    sensor_id: null,
  };
  containers.push(record);
  return record;
}

export function updateContainer(id: string, patch: Record<string, unknown>): StoredContainer | undefined {
  const c = containerRecordById(id);
  if (!c) return undefined;
  const writable = [
    'device_id', 'material_type_id', 'status', 'longitude', 'latitude', 'buffer', 'collection_site_id', 'height',
    'temperature', 'angle', 'full_alarm', 'fire_alarm', 'tilt_alarm', 'battery_alarm', 'volt', 'rsrp', 'frame_counter',
    'fill_level', 'organization_id',
  ];
  for (const key of writable) {
    if (patch[key] !== undefined) (c as unknown as Record<string, unknown>)[key] = patch[key];
  }
  if (patch.data && typeof patch.data === 'object') {
    c.data = { ...c.data, ...(patch.data as Record<string, unknown>) };
    if (typeof (patch.data as Record<string, unknown>).location_name === 'string') {
      c.data.location_name_ar = (patch.data as Record<string, unknown>).location_name;
    }
    const cap = Number(c.data.capacity ?? 0);
    if (cap > 0) c.capacity_kg = c.data.capacity_unit === 'tons' ? cap * 1000 : cap;
  }
  if (patch.fill_level !== undefined && c.capacity_kg > 0) {
    c.fill_percent = Math.min(100, Math.round((Number(patch.fill_level) / c.capacity_kg) * 100));
  }
  c.updated_at = nowIso();
  return c;
}

export function deleteContainer(id: string): boolean {
  const before = containers.length;
  containers = containers.filter((c) => c.id !== id);
  sensors.forEach((s) => {
    if (s.container_id === id) s.container_id = undefined;
  });
  return containers.length !== before;
}

/** Estimated kg in a container for a fill-level band (used to size new pickup orders). */
export function estimatedKgForFill(containerId: string | null | undefined, fillLevel: string | null | undefined): number {
  const c = containerRecordById(containerId);
  if (!c) return 0;
  const share = fillLevel === 'FULL' ? 1 : fillLevel === 'THREE_QT_FULL' ? 0.75 : fillLevel === 'HALF_FULL' ? 0.5 : fillLevel === 'QT_FULL' ? 0.25 : c.fill_percent / 100;
  return Math.max(5, Math.round((c.capacity_kg * share) / 5) * 5);
}

/** A pickup clears the bin: drop its fill level back to near-empty. */
export function markContainerEmptied(containerId: string | null | undefined): void {
  const c = containerRecordById(containerId);
  if (!c) return;
  c.fill_percent = 4;
  c.fill_level = Math.round(c.capacity_kg * 0.04);
  c.full_alarm = false;
  c.data = { ...c.data, fill_percent: 4, last_emptied_at: nowIso() };
  c.updated_at = nowIso();
}

// ── Sensors ────────────────────────────────────────────────────────────────

let sensors: SensorSchema[] = [];
const sensorCounter = makeCounter(100);

function signalPercent(dbm: number): number {
  return Math.max(5, Math.min(100, Math.round(((dbm + 110) / 55) * 100)));
}

function sensorRecord(s: DemoSensor, i: number): SensorSchema {
  const container = DEMO_CONTAINERS.find((c) => c.id === s.container_id)!;
  const site = DEMO_SITES.find((x) => x.id === s.site_id)!;
  const installed = daysAgo(400 - i * 21);
  const calibrated = daysAgo(40 + i * 9);
  return {
    id: s.id,
    name: `${site.code} · ${materialName(container.material_id)}`,
    type: SensorType.FILL_LEVEL,
    status: s.status === 'offline' ? SensorStatus.ERROR : SensorStatus.ACTIVE,
    serial_number: s.serial_number,
    model: s.model,
    manufacturer: 'Recyclee IoT',
    installation_date: iso(installed),
    last_calibration_date: iso(calibrated),
    next_calibration_date: iso(new Date(calibrated.getTime() + 180 * 86400000)),
    battery_level: s.battery_pct,
    signal_strength: signalPercent(s.signal_dbm),
    container_id: s.container_id,
    last_reading: { timestamp: s.last_seen_at, value: s.fill_level, unit: '%' },
    organization_id: DEMO_OUTLET_ORG_ID,
    created_at: iso(installed),
    updated_at: s.last_seen_at,
  };
}

export function listSensors(orgId: string, query: { search?: string; status?: string; type?: string }): SensorSchema[] {
  return sensors
    .filter((s) => s.organization_id === orgId)
    .filter((s) => !query.status || s.status === query.status)
    .filter((s) => !query.type || s.type === query.type)
    .filter((s) => {
      if (!query.search) return true;
      const q = query.search.toLowerCase();
      return s.name.toLowerCase().includes(q) || s.serial_number.toLowerCase().includes(q);
    });
}

export function sensorById(id: string): SensorSchema | undefined {
  return sensors.find((s) => s.id === id);
}

export function createSensor(input: Record<string, unknown>): SensorSchema {
  const n = sensorCounter.next();
  const sensor: SensorSchema = {
    id: ids.sensor(n),
    name: String(input.name ?? `Sensor ${n}`),
    type: (input.type as SensorType) ?? SensorType.FILL_LEVEL,
    status: SensorStatus.ACTIVE,
    serial_number: String(input.serial_number ?? `RCL-US-${String(3000 + n).padStart(5, '0')}`),
    model: String(input.model ?? 'Ultrasonic Fill Sensor US-200'),
    manufacturer: String(input.manufacturer ?? 'Recyclee IoT'),
    installation_date: String(input.installation_date ?? nowIso()),
    battery_level: 100,
    signal_strength: 80,
    container_id: input.container_id as string | undefined,
    organization_id: String(input.organization_id ?? DEMO_OUTLET_ORG_ID),
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  sensors.push(sensor);
  return sensor;
}

export function updateSensor(id: string, patch: Record<string, unknown>): SensorSchema | undefined {
  const s = sensorById(id);
  if (!s) return undefined;
  for (const key of ['name', 'type', 'status', 'last_calibration_date', 'next_calibration_date', 'container_id'] as const) {
    if (patch[key] !== undefined && patch[key] !== '') (s as unknown as Record<string, unknown>)[key] = patch[key];
  }
  s.updated_at = nowIso();
  return s;
}

export function deleteSensor(id: string): boolean {
  const before = sensors.length;
  sensors = sensors.filter((s) => s.id !== id);
  return sensors.length !== before;
}

export function calibrateSensor(id: string): SensorSchema | undefined {
  const s = sensorById(id);
  if (!s) return undefined;
  s.last_calibration_date = nowIso();
  s.next_calibration_date = iso(daysFromNow(180));
  if (s.status === SensorStatus.ERROR || s.status === SensorStatus.MAINTENANCE) s.status = SensorStatus.ACTIVE;
  s.updated_at = nowIso();
  return s;
}

/** Hourly fill readings for the last 48 h: a sawtooth that empties on pickup days. */
export function sensorReadings(id: string): SensorReading[] {
  const s = sensorById(id);
  if (!s) return [];
  const current = s.last_reading?.value ?? 50;
  const out: SensorReading[] = [];
  const end = Date.parse(s.last_reading?.timestamp ?? nowIso());
  const ratePerHour = 0.9 + (hashString(id) % 10) / 10;
  for (let h = 47; h >= 0; h -= 1) {
    let value = current - h * ratePerHour;
    while (value < 3) value += 72;
    out.push({
      sensor_id: id,
      timestamp: iso(new Date(end - h * HOUR_MS)),
      value: Math.round(Math.min(100, value)),
      unit: '%',
      metadata: { battery_level: s.battery_level, signal_strength: s.signal_strength, temperature: 24 + (h % 9) },
    });
  }
  return out;
}

export function addSensorReading(id: string, reading: SensorReading): SensorSchema | undefined {
  const s = sensorById(id);
  if (!s) return undefined;
  s.last_reading = { timestamp: reading.timestamp ?? nowIso(), value: reading.value, unit: reading.unit ?? '%' };
  s.updated_at = nowIso();
  return s;
}

export function sensorHealth(id: string) {
  const s = sensorById(id);
  if (!s) return null;
  const offline = s.status === SensorStatus.ERROR;
  return {
    status: offline ? 'offline' : (s.battery_level ?? 100) < 20 ? 'degraded' : 'healthy',
    details: {
      last_seen_at: s.last_reading?.timestamp ?? s.updated_at,
      signal_strength: s.signal_strength,
      battery_level: s.battery_level,
      firmware: 'US-200 v2.4.1',
      uptime_pct_30d: offline ? 71.4 : 99.2,
    },
  };
}

export function sensorBattery(id: string) {
  const s = sensorById(id);
  if (!s) return null;
  const level = s.battery_level ?? 100;
  return { level, status: level < 20 ? 'low' : level < 50 ? 'medium' : 'good', estimated_days_remaining: Math.round(level * 3.2) };
}

export function resetSites(): void {
  siteCounter.reset();
  containerCounter.reset();
  sensorCounter.reset();
  sites = DEMO_SITES.map(siteRecord);
  containers = DEMO_CONTAINERS.map(containerRecord);
  sensors = DEMO_SENSORS.map(sensorRecord);
}

export function siteName(siteId: string | null | undefined): string {
  return siteDisplayName(siteId) ?? '';
}

export function siteById(siteId: string | null | undefined): DemoSite | undefined {
  return DEMO_SITES.find((s) => s.id === siteId);
}
